import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  FormLabel,
  Link,
  Typography,
  InputAdornment,
  SvgIcon,
  IconButton,
  Grid,
} from '@mui/material';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Alert from '@Components/Alert';
import Button from '@Components/Button';
import {
  TextFieldWrapper,
  CheckboxWrapper,
} from '@Components/FormFieldsWrappers';
import Loader from '@Components/Loader';
import Config from '@Config';
import Icons from '@Constants/Icons.constants';
import { CommonConstants } from '@Constants/index';
import RoutesConstants from '@Constants/routes.constants';
import {
  useGetNewAccessTokenMutation,
  useLoginMutation,
  useValidateAccessTokenMutation,
} from '@Services/auth.service';
import TokenService from '@Services/token.service';
import { setCredentials } from '@Slices/auth.slice';
import {
  LoginRequest,
  LoginResponse,
  UserRoles,
  ValidateAccessTokenResponse,
} from '@Typings/auth.types';
import { BaseAlertProps, SSOLogin } from '@Typings/global.types';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import { useAppDispatch } from '@Utils/hooks/useStore.hook';

import { Container, LogoContainer } from './Login.styles';
import { LoginFormInputs } from './Login.types';
import { LoginSchema } from './LoginSchema';

const Login: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { triggerAlert } = useAlert();

  /** STATES */
  const [formState, setFormState] = useState<LoginRequest | undefined>();
  const [login, { isLoading }] = useLoginMutation();
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [validateAccessToken, { isLoading: isValidationInProgress }] =
    useValidateAccessTokenMutation();
  const [getNewAccessToken] = useGetNewAccessTokenMutation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSSOLogin, setisSSOLogin] = useState<SSOLogin>({
    refreshToken: null,
  });
  const [showLoader, setShowLoader] = useState<boolean>(false);

  /** Handles form validation using useForm hook */
  const methods = useForm<LoginFormInputs>({
    resolver: yupResolver(LoginSchema()),
    mode: 'onChange',
  });

  /**
   * Re-directs the user based on the user role
   */
  const redirectBasedOnUserRole = useCallback(
    (redirectToPath?: string) => {
      const userRole: UserRoles = TokenService.getUserRole();
      const pathNameAccordingToUserRole =
        userRole === 'admin'
          ? RoutesConstants.ADMIN_ROUTES.USERS
          : RoutesConstants.SCRIPTS_ROUTES.ACTIVE;
      const pathName = redirectToPath || pathNameAccordingToUserRole;

      // if redirectToPath contains query params
      if (pathName.split('?')[1]) {
        history.replace({
          pathname: pathName.split('?')[0], // pathname without query params
          search: pathName.split('?')[1], // query params
        });
      } else {
        const queryParams =
          userRole === 'admin'
            ? query.stringify({
                [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
                  CommonConstants.INITIAL_ROWS_PER_PAGE,
                [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
                  CommonConstants.INITIAL_PAGE_NO,
                [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
                  CommonConstants.QUERY_PARAMS.USERS.SORT_BY.ID,
              })
            : query.stringify({
                [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
                  CommonConstants.INITIAL_ROWS_PER_PAGE,
                [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
                  CommonConstants.INITIAL_PAGE_NO,
                [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
                  CommonConstants.INITIAL_SORT_ORDER,
              });

        history.replace({
          pathname: pathName.split('?')[0],
          search: queryParams,
        });
      }
    },
    [history]
  );

  // check if the route has "sso"
  const checkForSSO = useCallback(() => {
    const tokenParam =
      query.get(CommonConstants.QUERY_PARAMS.COMMON.TOKEN) || '';
    const redirectTo =
      query.get(CommonConstants.QUERY_PARAMS.COMMON.REDIRECT_TO) || '';

    /** If logged in already, redirect to default page according to user role type, if its not SSO */
    if (
      TokenService.getAccessToken() &&
      (history.location.pathname === RoutesConstants.GLOBAL_ROUTES.LOGIN ||
        (history.location.pathname === RoutesConstants.GLOBAL_ROUTES.SSO &&
          !tokenParam))
    ) {
      redirectBasedOnUserRole();
    } else if (
      tokenParam &&
      !TokenService.isTokenExpired(tokenParam.toString())
    ) {
      // if token is present with the SSO and it is a valid token
      setShowLoader(true);
      if (redirectTo) {
        setisSSOLogin({
          refreshToken: tokenParam.toString(),
          redirectToPath: redirectTo.toString(),
        });
      } else {
        setisSSOLogin({
          refreshToken: tokenParam.toString(),
        });
      }
    } else {
      // if the user tries to hit the /sso path without the token, redirect the user to Login
      history.push(RoutesConstants.GLOBAL_ROUTES.LOGIN);
    }
  }, [redirectBasedOnUserRole, history]);

  // check for SSO login
  useEffect(() => {
    checkForSSO();
  }, [checkForSSO]);

  useEffect(() => {
    async function log() {
      const response = formState
        ? await login(formState).unwrap()
        : await getNewAccessToken(isSSOLogin.refreshToken).unwrap();
      const validateTokenResponse: ValidateAccessTokenResponse =
        await validateAccessToken(response.accessToken).unwrap();
      if (validateTokenResponse.isValid) {
        dispatch(
          setCredentials({
            ...response,
            isLoggedIn: true,
            role: validateTokenResponse.isAdmin
              ? (CommonConstants.USER_ROLES.ADMIN as UserRoles)
              : (CommonConstants.USER_ROLES.USER as UserRoles),
          })
        );
        setShowLoader(false);
        redirectBasedOnUserRole(isSSOLogin.redirectToPath);
      }
    }
    if (formState || isSSOLogin.refreshToken) {
      log()
        .then(() => {})
        .catch((error: { status: number; data: LoginResponse }) => {
          if (error?.data?.error) {
            triggerAlert(error.data.error, setAlertState, 'error');
          }
          setShowLoader(false);
        });
    }

    return () => {
      setAlertState({
        openAlert: false,
      });
    };
  }, [
    dispatch,
    login,
    validateAccessToken,
    triggerAlert,
    redirectBasedOnUserRole,
    getNewAccessToken,
    history,
    formState,
    isSSOLogin.refreshToken,
    isSSOLogin.redirectToPath,
  ]);

  /* Reset Alert */
  const resetAlert = () => {
    if (alertState?.resetAlert) {
      alertState.resetAlert((prevState) => ({
        ...prevState,
        openAlert: false,
      }));
    }
  };

  /** Handles Password visibility on icon click */
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmitHandler: SubmitHandler<LoginFormInputs> = (data) => {
    setFormState({
      login: data.emailUsername,
      password: data.password,
    });
  };

  /** TERMS AND CONDITIONS CHECKBOXES ERROR */
  const checkboxError =
    methods.formState.errors.dncRegistry ||
    methods.formState.errors.termsAndCondition;

  return (
    <Container container columns={16}>
      <Grid item className="infoPanel" xs={8}>
        <Box sx={{ maxWidth: '54.4rem', margin: 'auto' }}>
          <LogoContainer />
          <Typography
            variant="h2"
            fontSize={20}
            fontWeight={400}
            color="common.white"
            mb={3.2}
          >
            {t('login.welcome')}
          </Typography>
          <Typography variant="main" color="common.white" mb={4.7}>
            {t('login.welcome_desc')}
          </Typography>
          <Typography variant="main" color="common.white" maxWidth="41.2rem">
            {t('login.Not a user')}
          </Typography>
          <Typography variant="main" color="common.white" maxWidth="41.2rem">
            <Trans
              i18nKey="login.register_text"
              components={{
                register: (
                  <Link
                    href={
                      Config?.REACT_APP_ENV_NAME === 'qa'
                        ? CommonConstants.EXTERNAL_URL.QA.REGISTRATION_URL
                        : CommonConstants.EXTERNAL_URL.PROD.REGISTRATION_URL
                    }
                    target="_blank"
                    underline="always"
                    rel="noreferrer"
                    color="common.white"
                  />
                ),
              }}
            />
          </Typography>
        </Box>
      </Grid>
      <Grid item className="loginPanel" px={13.2} py={12.3} xs={8}>
        <Box sx={{ maxWidth: '45.6rem', margin: 'auto' }}>
          <Typography variant="h2" fontSize={20} fontWeight={400} mb={2.4}>
            {`${t('login.Please')} `}
            {t('login.Log in')}
          </Typography>
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={methods.handleSubmit(onSubmitHandler)}
            >
              <Box display="flex" flexDirection="column" mb={4}>
                <FormLabel
                  sx={{
                    color: methods.formState.errors.emailUsername
                      ? 'error.main'
                      : 'text.primary',
                    ml: 1,
                    mb: 0.4,
                  }}
                >
                  {`${t('login.Username or Email')} *`}
                </FormLabel>
                <TextFieldWrapper
                  name="emailUsername"
                  disabled={isLoading || isValidationInProgress || showLoader}
                  placeholder={t('login.Username or Email')}
                />
              </Box>
              <Box display="flex" flexDirection="column" mb={3.3}>
                <FormLabel
                  sx={{
                    color: methods.formState.errors.password
                      ? 'error.main'
                      : 'text.primary',
                    ml: 1,
                    mb: 0.4,
                  }}
                >
                  {`${t('login.Password')} *`}
                </FormLabel>
                <TextFieldWrapper
                  name="password"
                  disabled={isLoading || isValidationInProgress || showLoader}
                  placeholder={t('login.password_placeholder')}
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ m: '0' }}>
                        <IconButton
                          sx={{ m: '0', mx: '0.4' }}
                          onClick={handleClickShowPassword}
                        >
                          <SvgIcon>
                            {showPassword
                              ? Icons.visibility
                              : Icons.visibilityOff}
                          </SvgIcon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Button
                type="submit"
                fullWidth
                sx={{ mb: 2.4, position: 'relative' }}
                disabled={isLoading || isValidationInProgress || showLoader}
              >
                {(isLoading || isValidationInProgress || showLoader) && (
                  <Loader
                    size="2.5rem"
                    sx={{
                      position: 'absolute',
                      color: 'common.black',
                    }}
                  />
                )}
                {t('login.login')}
              </Button>
              <Box mb={1.6}>
                <CheckboxWrapper
                  name="termsAndCondition"
                  disabled={isLoading || isValidationInProgress || showLoader}
                  label={
                    <Typography variant="main">
                      <Trans
                        i18nKey="login.service_text"
                        components={{
                          terms: (
                            <Link
                              href={
                                Config?.REACT_APP_ENV_NAME === 'qa'
                                  ? CommonConstants.EXTERNAL_URL.QA
                                      .TERMS_AND_CONDITION
                                  : CommonConstants.EXTERNAL_URL.PROD
                                      .TERMS_AND_CONDITION
                              }
                              target="_blank"
                              underline="always"
                              rel="noreferrer"
                              color="textSecondary"
                            />
                          ),
                          privacy: (
                            <Link
                              href={
                                Config?.REACT_APP_ENV_NAME === 'qa'
                                  ? CommonConstants.EXTERNAL_URL.QA
                                      .PRIVACY_POLICY
                                  : CommonConstants.EXTERNAL_URL.PROD
                                      .PRIVACY_POLICY
                              }
                              target="_blank"
                              underline="always"
                              rel="noreferrer"
                              color="textSecondary"
                            />
                          ),
                        }}
                      />
                      {` *`}
                    </Typography>
                  }
                />
              </Box>
              <Box mb={!checkboxError ? 2.4 : 1.6}>
                <CheckboxWrapper
                  name="dncRegistry"
                  disabled={isLoading || isValidationInProgress || showLoader}
                  label={
                    <Typography variant="main">
                      <Trans
                        i18nKey="login.registry_text"
                        components={{
                          consumer: (
                            <Link
                              href={
                                Config?.REACT_APP_ENV_NAME === 'qa'
                                  ? CommonConstants.EXTERNAL_URL.QA.CONSUMER
                                  : CommonConstants.EXTERNAL_URL.PROD.CONSUMER
                              }
                              target="_blank"
                              underline="always"
                              rel="noreferrer"
                              color="textSecondary"
                            />
                          ),
                          dncRegistry: (
                            <Link
                              href={
                                Config?.REACT_APP_ENV_NAME === 'qa'
                                  ? CommonConstants.EXTERNAL_URL.QA.DNC_REGISTRY
                                  : CommonConstants.EXTERNAL_URL.PROD
                                      .DNC_REGISTRY
                              }
                              target="_blank"
                              underline="always"
                              rel="noreferrer"
                              color="textSecondary"
                            />
                          ),
                        }}
                      />
                      {` *`}
                    </Typography>
                  }
                />
              </Box>
            </Box>
            {checkboxError && (
              <Box mb={1.6}>
                <Typography variant="helpText" color="error">
                  {t('common.formErrorMessages.accept_tnc')}
                </Typography>
              </Box>
            )}
          </FormProvider>
          <Typography variant="main" mb={2.4} textAlign="center">
            {`${t('login.Not a user')} `}
            <Link
              href={
                Config?.REACT_APP_ENV_NAME === 'qa'
                  ? CommonConstants.EXTERNAL_URL.QA.REGISTRATION_URL
                  : CommonConstants.EXTERNAL_URL.PROD.REGISTRATION_URL
              }
              target="_blank"
              underline="always"
              rel="noreferrer"
              color="secondary"
            >
              {t('login.Create an account')}
            </Link>
          </Typography>
          <Typography variant="helpText" textAlign="center">
            <Trans
              i18nKey="login.login_footer"
              components={{
                ccpa: (
                  <Link
                    href={
                      Config?.REACT_APP_ENV_NAME === 'qa'
                        ? CommonConstants.EXTERNAL_URL.QA.CCPA
                        : CommonConstants.EXTERNAL_URL.PROD.CCPA
                    }
                    target="_blank"
                    underline="always"
                    rel="noreferrer"
                    color="textSecondary"
                  />
                ),
              }}
            />
          </Typography>
        </Box>
      </Grid>
      <Alert
        open={alertState.openAlert}
        autoHideDuration={CommonConstants.ALERT_HIDING_TIME}
        text={alertState.message}
        severity={alertState.severity}
        closeBtnProps={{
          onClick: resetAlert,
        }}
        onClose={resetAlert}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
      />
    </Container>
  );
};

export default Login;
